<script setup lang="ts">
import { Head, useForm } from "@inertiajs/vue3"
import { computed } from "vue"

import FlashAlerts from "@/components/FlashAlerts.vue"
import NarrowPage from "@/components/NarrowPage.vue"
import { useNavbar } from "@/composables/useNavbar"
import { ICON } from "@/lib/icons"

type AccessTokenPermission = "read" | "write"

const props = defineProps<{
  permissions: AccessTokenPermission[]
}>()

const { title } = useNavbar({ title: "New API Token" })

const form = useForm<{
  description: string
  permission: AccessTokenPermission
}>({
  description: "",
  permission: "read",
})

const permissionOptions = computed(() =>
  props.permissions.map((permission) => ({
    value: permission,
    label: permission === "read" ? "Read" : "Write",
    description:
      permission === "read" ? "Can search and view inventory." : "Can view and change inventory.",
  })),
)

function submit() {
  form
    .transform((data) => ({ access_token: data }))
    .post("/account/access_tokens", { preserveScroll: false })
}
</script>

<template>
  <Head :title="title" />

  <NarrowPage>
    <FlashAlerts />

    <form
      action="/account/access_tokens"
      method="post"
      class="flex flex-col gap-6"
      @submit.prevent="submit"
    >
      <UFormField
        name="description"
        label="What is this token for?"
        description="Use a name that identifies the agent, computer, or script."
        :error="form.errors.description"
        required
      >
        <UInput
          v-model="form.description"
          name="description"
          placeholder="Codex on this Mac"
          maxlength="100"
          autocomplete="off"
          autofocus
          required
          class="w-full"
        />
      </UFormField>

      <UFormField
        name="permission"
        label="Permission"
        description="Start with read access unless the agent needs to make changes."
        :error="form.errors.permission"
        required
      >
        <URadioGroup v-model="form.permission" name="permission" :items="permissionOptions" />
      </UFormField>

      <UAlert
        :icon="ICON.apiToken"
        color="warning"
        variant="subtle"
        title="The token is shown only once"
        description="Copy it immediately after creation. Ninjanizr stores only a secure digest."
      />

      <div class="flex items-center gap-4">
        <UButton to="/account/access_tokens" label="Cancel" color="neutral" variant="outline" />
        <UButton type="submit" label="Create token" :loading="form.processing" />
      </div>
    </form>
  </NarrowPage>
</template>
